<template>
  <section class="flex flex-col md:flex-row w-full px-4 md:px-0 gap-6">
    <div class="flex-1">
      <img
        :src="`https://iwcdeped.com/gazetteimages/${itemDetails.filename}`"
        alt="Banner"
        class="rounded-lg object-fill h-auto w-full"
      />
      <div class="py-4">
        <div class="space-y-1">
          <h1
            class="text-primarydark font-headline font-extrabold py-3 text-2xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight"
          >
            {{ itemDetails.title }}
          </h1>
          <h3 class="text-sm text-zinc-500">{{ renderDateTimeFormat(itemDetails.createdAt) }}</h3>
        </div>

        <p class="py-6 leading-9 break-words" v-html="sanitizeHtml(itemDetails.content)"></p>
      </div>
    </div>

    <!-- RIGHT SIDE -->
    <div class="hidden md:block w-96 flex-shrink-0">
      <div class="h-auto lg:h-72">
        <div class="flex gap-2 items-center">
          <h1 class="text-lg sm:text-xl lg:text-2xl font-semibold text-primarydark">
            Related Highlights
          </h1>
        </div>

        <div class="py-4 sm:py-6 space-y-3 text-sm">
          <ul>
            <li v-for="(item, index) in relatedArticles" :key="index" class="max-h-auto">
              <div class="flex gap-4">
                <!-- Image -->
                <img
                  :src="`https://iwcdeped.com/gazetteimages/${item.filename}`"
                  alt="Banner"
                  class="h-28 w-36 rounded-lg object-cover flex-shrink-0"
                />

                <!-- Content -->
                <router-link
                  :to="`/portal/home/article/${encryptFile(item.id)}`"
                  class="space-y-2 flex-1"
                >
                  <h1 class="text-lg font-semibold text-gray-900">
                    {{ item.title }}
                  </h1>

                  <h2 class="text-xs text-zinc-500">
                    {{ renderDateTimeFormat(item.createdAt) }}
                  </h2>
                </router-link>
              </div>

              <hr
                class="my-6 border-0 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"
              />
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import reusableBase from '@/utils/reusables/dateFormat';
import encryptHandler from '@/utils/encrypt';
import DOMPurify from 'dompurify';
import useDecryptParam from '@/utils/decrypt';
import { onMounted, ref, inject, computed, watch } from 'vue';

export default {
  setup() {
    const { decryptedValue: id } = useDecryptParam('id');
    const { encryptFile } = encryptHandler();

    const { renderDateTimeFormat } = reusableBase();
    const axios = inject('$axios');

    const itemDetails = ref({});
    const itemHighlights = ref([]);

    /* ---------------- FETCH SINGLE ARTICLE ---------------- */
    const fetchArticle = async () => {
      try {
        const result = await axios.get(`/api/articles/${id.value}`);
        itemDetails.value = result.data.data;
      } catch (error) {
        console.log('fetchArticle error:', error);
      }
    };

    /* ---------------- FETCH HIGHLIGHTS ---------------- */
    const fetchHighlights = async () => {
      try {
        const result = await axios.get(`/api/articles`, {
          params: { highlight: true }
        });
        itemHighlights.value = result.data.data;
      } catch (error) {
        console.log('fetchHighlights error:', error);
      }
    };

    /* ---------------- LOAD BOTH ---------------- */
    const loadData = async () => {
      await fetchArticle();
      await fetchHighlights();
    };

    /* ---------------- FIRST LOAD ---------------- */
    onMounted(loadData);

    /* ---------------- WATCH ROUTE PARAM CHANGE ---------------- */
    watch(id, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        loadData();
      }
    });

    /* ---------------- FILTER RELATED ---------------- */
    const relatedArticles = computed(() => {
      return itemHighlights.value.filter((val) => val.id !== Number(id.value));
    });

    /* ---------------- SANITIZER ---------------- */
    const sanitizeHtml = (html) => {
      if (!html) return '';

      html = html.replace(/&nbsp;/g, ' ');
      html = html.replace(/\s{2,}/g, ' ');
      html = html.replace(/<p>(\s|<br\s*\/?>)*<\/p>/gi, '');
      html = html.trim();

      return DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true }
      });
    };

    return {
      id,
      itemDetails,
      itemHighlights,
      relatedArticles,
      sanitizeHtml,
      encryptFile,
      renderDateTimeFormat
    };
  }
};
</script>
